import { useEffect, useState } from 'react';
import { CognitoAuth } from 'amazon-cognito-auth-js';
import { useCookies } from 'react-cookie';
import { appConfig } from '../localDev/config';

const useEffectOnlyOnce = (func) => useEffect(func, []);

function removeQueryFromLocation() {
    if (window.history.length > 0) {
        const newHref = window.location.href.split('?')[0];
        window.history.replaceState(undefined, 'Oncall Reminders', newHref);
    }
}

export default function useCognitoAuth() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authToken, setAuthToken] = useState('');
    const [userAlias, setUserAlias] = useState('');
    const [cookies, setCookie, removeCookie] = useCookies(['url']);

    const setUrlCookie = (value) => {
        if (window.location.href.indexOf('?') > 0) {
            setCookie('url', value, { path: '/', sameSite: 'lax' });
        }
    };

    const removeUrlCookie = () => {
        removeCookie('url');
    };

    const handleAuthSuccess = (cognitoAuth) => {
        const sessionToken = cognitoAuth.getSignInUserSession().getIdToken();
        const authToken = sessionToken.getJwtToken();
        setAuthToken(authToken);
        setUserAlias(sessionToken.payload.identities?.[0]?.userId || 'unknown');
        setIsAuthenticated(true);
    };

    const handleAuthFailure = () => {
        setIsAuthenticated(false);
    };

    const initCognitoAuth = (cognitoConfig, onSuccess, onFailure) => {
        const fullCognitoConfig = {
            ClientId: cognitoConfig.ClientId,
            AppWebDomain: cognitoConfig.AppWebDomain,
            TokenScopesArray: cognitoConfig.TokenScopesArray,
            RedirectUriSignIn: window.location.origin,
            RedirectUriSignOut: window.location.origin,
            UserPoolId: cognitoConfig.UserPoolId,
        };
        const auth = new CognitoAuth(fullCognitoConfig);
        auth.useCodeGrantFlow();
        auth.userhandler = {
            onFailure: () => {
                setUrlCookie(window.location.href);
                removeQueryFromLocation();
                auth.signOut();
                onFailure();
            },
            onSuccess: () => {
                removeQueryFromLocation();
                if (cookies.url) {
                    window.location.href = cookies.url;
                    removeUrlCookie();
                }
                onSuccess(auth);
            },
        };

        const { href } = window.location;
        const session = auth.getSignInUserSession();

        if (session.isValid()) {
            onSuccess(auth);
        } else if (href.indexOf('?code') > 0) {
            auth.parseCognitoWebResponse(href);
        } else {
            setUrlCookie(window.location.href);
            auth.getSession();
        }
    };

    useEffectOnlyOnce(() => {
        if (appConfig.mock.useMockAuth) {
            setIsAuthenticated(true);
            setUserAlias(appConfig.mock.user.alias);
            return;
        }

        const fetchOptions = async (file) => {
            await fetch(file)
                .then(response => response.json())
                .then(data => initCognitoAuth(data, handleAuthSuccess, handleAuthFailure));
        };
        window.location.origin === 'http://localhost:3000' ? fetchOptions('/localSettings.json') : fetchOptions('/settings.json');
    });

    return { isAuthenticated, authToken, userAlias };
}
